import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu } from "antd";
import {
  AudioOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  StarOutlined,
  SyncOutlined,
  CodeOutlined,
  FileImageOutlined,
  UserOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import logo from "../assets/logo.svg";
import logoSymbol from "../assets/logoSymbol.svg";
import { authService } from "../services/authService";
import { useSubscription } from "../hooks/useSubscription";
import { useUserProfile } from "../hooks/useUserProfile";

const { Sider } = Layout;

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshSubscription } = useSubscription();
  const { isDeveloper, isLoading: profileLoading, profile } = useUserProfile();

  // Only refresh subscription on initial mount, not on re-renders
  useEffect(() => {
    if (!initialLoadDone) {
      refreshSubscription();
      setInitialLoadDone(true);
    }
  }, [initialLoadDone, refreshSubscription]);

  const handleRefreshSubscription = useCallback(async () => {
    await refreshSubscription();
  }, [refreshSubscription]);

  // Filter menu items based on user profile type
  const allMenuItems = [
    {
      key: "visit",
      icon: <AudioOutlined />,
      label: "New Visit",
      path: "/visit",
    },
    {
      key: "mynotes",
      icon: <FileTextOutlined />,
      label: "My Notes",
      path: "/mynotes",
    },
    {
      key: "patients",
      icon: <UserOutlined />,
      label: "Patients",
      path: "/patients",
    },
    {
      key: "images",
      icon: <FileImageOutlined />,
      label: "Images Analysis",
      path: "/images",
    },
    {
      key: "ai-studio",
      icon: <StarOutlined />,
      label: "AI Studio",
      path: "/ai-studio",
    },
    ...(isDeveloper ? [{
      key: "developer",
      icon: <CodeOutlined />,
      label: "API Keys",
      path: "/developer",
    }, {
      key: "search-templates",
      icon: <DatabaseOutlined />,
      label: "Search Templates",
      path: "/search-templates",
    }, {
      key: "docs",
      icon: <FileTextOutlined />,
      label: "Docs",
      path: "/docs",
    }, {
      key: "prompts",
      icon: <FileTextOutlined />,
      label: "Prompts",
      path: "/prompts",
    }, {
      key: "analytics",
      icon: <SyncOutlined />,
      label: "Analytics",
      path: "/analytics",
    }] : []),
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      path: "/settings",
    },
  ];

  // Hide "My Notes" and "New Visit" for developer profile
  const menuItems = allMenuItems.filter(item => {
    console.log('Dashboard - Profile filtering debug:', {
      isDeveloper,
      profileLoading,
      itemKey: item.key,
      profileType: localStorage.getItem('profile_type'),
      cachedProfile: localStorage.getItem('user_profile')
    });

    if (isDeveloper) {
      console.log(`Dashboard - Hiding ${item.key} for developer profile`);
      // Hide My Notes, New Visit and AI Studio from developer users
      return item.key !== "mynotes" && item.key !== "visit" && item.key !== "ai-studio";
    }
    return true;
  });

  const handleMenuClick = (key: string) => {
    const item = menuItems.find((item) => item.key === key);
    if (item) {
      navigate(item.path);
    } else if (key === "logout") {
      authService.logout();
    } else if (key === "refresh") {
      handleRefreshSubscription();
    }
  };

  const selectedKey = menuItems.find(
    (item) => item.path === location.pathname
  )?.key;

  return (
    <Sider
      collapsible={true}
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      theme="light"
      width={220}
      className="min-h-screen shadow-sm transition-all duration-300 ease-in-out flex flex-col [&_.ant-layout-sider-trigger]:!bg-[#f5f5f7] [&_.ant-layout-sider-trigger]:!text-gray-500 [&_.ant-layout-sider-trigger:hover]:!text-gray-700  ]"
      style={{
        borderRight: "1px solid rgba(0, 0, 0, 0.1)",
        backgroundColor: "#f5f5f7",
      }}
    >
      <div className="h-16 flex items-center justify-between px-4 border-b border-[rgba(0,0,0,0.1)] bg-[#f5f5f7]">
        {isDeveloper ? (
          // Show organization name for developers
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-lg">
              <span className="text-white font-bold text-sm">
                {(profile?.organization_name || profile?.company_name || profile?.name || 'DEV').charAt(0).toUpperCase()}
              </span>
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <h2 className="text-sm font-semibold text-gray-800 truncate max-w-[140px]">
                  {profile?.organization_name || profile?.company_name || profile?.name || 'Developer Org'}
                </h2>
                <p className="text-xs text-gray-500">Developer Portal</p>
              </div>
            )}
          </div>
        ) : (
          // Show DocuMD logo for regular users
          <img
            src={collapsed ? logoSymbol : logo}
            alt="DocuMD"
            className={`${
              collapsed ? "w-8" : "w-[120px]"
            } transition-all duration-300 ease-in-out object-contain`}
          />
        )}
      </div>

   

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        items={menuItems}
        onClick={({ key }) => handleMenuClick(key)}
        className="!bg-[#f5f5f7] flex-1"
      />

      <div className="mt-auto">
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[]}
          items={[
            {
              key: "logout",
              icon: <LogoutOutlined />,
              label: "Sign Out",
              className:
                "text-red-600 hover:text-red-700 [&_.anticon]:text-red-600 hover:[&_.anticon]:text-red-700",
            },
          ]}
          onClick={({ key }) => handleMenuClick(key)}
          className="!bg-[#f5f5f7] border-t border-[rgba(0,0,0,0.1)]"
        />
      </div>
    </Sider>
  );
};

export default Dashboard;
